/**
 * @fileoverview Фабрика начального состояния смены.
 */

import { FRONT_STAGES } from "../constants/front";
import { createGates, createRunways, createZoneDefs } from "../constants/layout";
import { modeCfg, type ModeId } from "../constants/modes";
import { W } from "../constants/world";
import type { FrontState, GameState, Zone } from "./types";
import { rnd } from "../util/math";

/**
 * Создать объект снежного фронта под конфиг режима.
 * @param frontMul — множитель `cfg.front`
 */
export function createFront(frontMul: number): FrontState {
  const dir = Math.random() < 0.5 ? 1 : -1;
  return {
    x: dir > 0 ? -200 : W + 200,
    y: 360,
    dir,
    speed: (38 + rnd(0, 14)) * frontMul,
    width: 280 + rnd(0, 80),
    intensity: 0.55,
    stage: 0,
    stageT: 0,
    stageDur: (55) / frontMul,
    forecastBias: rnd(-0.2, 0.25),
    wave: 1,
  };
}

/**
 * Runtime-зоны снега из статических определений.
 */
export function createZones(): Zone[] {
  return createZoneDefs().map((z) => ({
    ...z,
    snow: z.kind === "rwy" ? (z.rwy === 0 ? 22 : 8) : rnd(4, 14),
    ice: rnd(0, 6),
    load: 0,
    open: true,
    closedReason: "",
  }));
}

/**
 * Создать полное состояние новой смены.
 *
 * @param modeId — режим сложности / длительности
 * @param tutorial — учебная смена (без спавна волны)
 */
export function createGame(modeId: ModeId = "quick", tutorial = false): GameState {
  const cfg = modeCfg(modeId);
  return {
    mode: tutorial ? "tutorial" : "play",
    modeId: cfg.id,
    cfg,
    time: cfg.endless ? 0 : cfg.time,
    timeAlive: 0,
    running: true,
    over: false,
    score: 0,
    streak: 0,
    mult: 1,
    bestStreak: 0,
    landings: 0,
    departures: 0,
    goarounds: 0,
    violations: 0,
    delays: 0,
    cancels: 0,
    taxiHits: 0,
    waitSum: 0,
    waitCount: 0,
    safety: 100,
    minSafety: 100,
    reagent: 100 * cfg.recover,
    reagentUsed: 0,
    clearLockT: 0,
    takeoffLockT: 0,
    planes: [],
    zones: createZones(),
    front: createFront(cfg.front),
    floats: [],
    sparks: [],
    hazards: [],
    spawnT: tutorial ? 9999 : 2.5,
    eventT: 22 / cfg.events,
    decision: null,
    snowRate: 1,
    snowBoost: 0,
    snowBoostT: 0,
    vis: 0.08,
    visTarget: 0.08,
    visT: 0,
    windDir: -0.55 + rnd(-0.3, 0.3),
    windSpd: 10 + rnd(0, 8),
    cross: 0,
    crossT: 0,
    wxName: "Умеренный снег",
    scenarioId: "free",
    scenarioLabel: "",
    riskyOps: 0,
    paxMood: 80,
    bestDecision: "",
    worstMistake: "",
    storyBits: [],
    scoreLog: [],
    flash: 0,
    shake: 0,
    elapsed: 0,
    idc: 1,
    tutStep: tutorial ? 0 : -1,
    bothClosedFlag: false,
    runways: createRunways(),
    gates: createGates(),
  };
}

/** Текущая стадия фронта (таблица FRONT_STAGES). */
export function frontStage(g: GameState) {
  return FRONT_STAGES[Math.min(g.front.stage, FRONT_STAGES.length - 1)]!;
}
